import { useEffect } from "react";
import { ImportJobResult, ImportJobStatus } from "./import";

export type FinishHandler = (
  status: ImportJobStatus,
  result: ImportJobResult,
) => void | Promise<void>;

export type ContinueHandler = (status: ImportJobStatus) => void;

const finishHandlers = new Map<string, FinishHandler>();
const continueHandlers = new Map<string, ContinueHandler>();

/**
 * useImportJob registers its consumer's onFinish here so the session-scoped
 * ImportPoller can dispatch when a job completes, even if the consumer page
 * isn't currently mounted. Cleanup on unmount only removes the handler if
 * it's still the current registration (later mounts shouldn't be wiped).
 */
export const useFinishHandler = (
  serviceJobId: string,
  handler?: FinishHandler,
) => {
  useEffect(() => {
    if (!handler) return;
    finishHandlers.set(serviceJobId, handler);
    return () => {
      if (finishHandlers.get(serviceJobId) === handler) {
        finishHandlers.delete(serviceJobId);
      }
    };
  }, [serviceJobId, handler]);
};

export const useContinueHandler = (
  serviceJobId: string,
  handler?: ContinueHandler,
) => {
  useEffect(() => {
    if (!handler) return;
    continueHandlers.set(serviceJobId, handler);
    return () => {
      if (continueHandlers.get(serviceJobId) === handler) {
        continueHandlers.delete(serviceJobId);
      }
    };
  }, [serviceJobId, handler]);
};

export const getFinishHandler = (serviceJobId: string) =>
  finishHandlers.get(serviceJobId);

export const getContinueHandler = (serviceJobId: string) =>
  continueHandlers.get(serviceJobId);
